import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Circle, Plus, Sparkles, GripVertical, ChevronDown, ChevronRight, X, Calendar, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Subtasks section for a task. Shows the "Sledeći korak" prominently plus
 * a collapsed list of the rest. Adds via a single input; optional details
 * (assignee, due date) hidden behind "Dodaj detalje" toggle. Drag-drop
 * reorder. AI breakdown on demand.
 */
export default function SubtasksSection({ task, contacts = [], onChanged, onCompleteMain }) {
  const [subtasks, setSubtasks] = useState(task?.subtasks || []);
  const [nextOverride, setNextOverride] = useState(task?.next_subtask_id || null);
  const [expanded, setExpanded] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [dragId, setDragId] = useState(null);

  useEffect(() => {
    setSubtasks(task?.subtasks || []);
    setNextOverride(task?.next_subtask_id || null);
  }, [task?.id, task?.subtasks, task?.next_subtask_id]);

  const internalContacts = contacts.filter((c) => c.kind === "internal_user");
  const contactsById = Object.fromEntries(contacts.map((c) => [c.id, c]));

  const sorted = [...subtasks].sort((a, b) => a.order - b.order);
  const pending = sorted.filter((s) => !s.done);
  const done = sorted.filter((s) => s.done);
  const nextSub = nextOverride
    ? sorted.find((s) => s.id === nextOverride && !s.done)
    : pending[0];
  const allDone = subtasks.length > 0 && pending.length === 0;
  // Auto-collapse when >3 subtasks — reduce cognitive load per Product feedback.
  const hasHiddenSteps = subtasks.length - (nextSub ? 1 : 0) > 0;

  const refresh = async () => {
    try {
      const { data } = await api.get(`/tasks/${task.id}`);
      setSubtasks(data.subtasks || []);
      setNextOverride(data.next_subtask_id || null);
    } catch {}
  };

  const addQuick = async (title) => {
    if (!title.trim()) return;
    try {
      await api.post(`/tasks/${task.id}/subtasks`, { title: title.trim() });
      await refresh();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleDone = async (s) => {
    try {
      const { data } = await api.patch(`/tasks/${task.id}/subtasks/${s.id}`, { done: !s.done });
      await refresh();
      onChanged?.();
      if (data.all_subtasks_done) setShowBanner(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeSub = async (sid) => {
    try {
      await api.delete(`/tasks/${task.id}/subtasks/${sid}`);
      await refresh();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setAsNext = async (sid) => {
    try {
      await api.patch(`/tasks/${task.id}/subtasks/set-next`, { subtask_id: sid });
      setNextOverride(sid);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const patchSub = async (sid, patch) => {
    try {
      await api.patch(`/tasks/${task.id}/subtasks/${sid}`, patch);
      await refresh();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDragStart = (id) => setDragId(id);
  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = sorted.map((s) => s.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setDragId(null);
    try {
      await api.patch(`/tasks/${task.id}/subtasks/reorder`, { ordered_ids: ids });
      await refresh();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const stepsWord = (n) =>
    n === 1 ? "korak" : n >= 2 && n <= 4 ? "koraka" : "koraka";

  return (
    <section data-testid="subtasks-section" className="space-y-2">
      {/* Next step (main focus) — the ONE thing user should notice first */}
      {nextSub && (
        <div className="rounded-lg border border-forest/20 bg-forest/[0.02] px-4 py-4" data-testid="next-step-bar">
          <div className="text-[10px] text-forest/70 font-semibold uppercase tracking-[0.14em]">Sledeći korak</div>
          <div className="flex items-start gap-3 mt-2">
            <button
              type="button"
              onClick={() => toggleDone(nextSub)}
              className="mt-1 shrink-0"
              data-testid={`subtask-toggle-${nextSub.id}`}
              aria-label="Označi kao završeno"
            >
              <Circle className="w-5 h-5 text-ink-muted hover:text-forest transition-colors" />
            </button>
            <div className="flex-1 min-w-0 text-base leading-snug text-ink">
              {nextSub.title}
              {(nextSub.assignee_contact_id || nextSub.due_date) && (
                <div className="text-[11px] text-ink-muted mt-1 flex flex-wrap gap-2">
                  {nextSub.assignee_contact_id && (
                    <span className="inline-flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {contactsById[nextSub.assignee_contact_id]?.name || "?"}
                    </span>
                  )}
                  {nextSub.due_date && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />{nextSub.due_date}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Collapsed / expand toggle — hidden count of remaining steps */}
      {hasHiddenSteps && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="subtasks-expand-btn"
          className="text-xs text-ink-muted hover:text-forest inline-flex items-center gap-1"
        >
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          {expanded ? "Sakri korake" : `Još ${subtasks.length - (nextSub ? 1 : 0)} ${stepsWord(subtasks.length - (nextSub ? 1 : 0))}`}
          <span className="ml-2 text-ink-muted">·  {done.length} od {subtasks.length}</span>
        </button>
      )}

      {/* Expanded full list */}
      {expanded && (
        <ul className="space-y-1 border border-line rounded-md p-2" data-testid="subtasks-list">
          {sorted.map((s) => (
            <SubtaskRow
              key={s.id}
              s={s}
              isNext={nextSub?.id === s.id}
              internalContacts={internalContacts}
              contactsById={contactsById}
              onToggle={() => toggleDone(s)}
              onRemove={() => removeSub(s.id)}
              onSetNext={() => setAsNext(s.id)}
              onPatch={(patch) => patchSub(s.id, patch)}
              onDragStart={() => onDragStart(s.id)}
              onDrop={() => onDrop(s.id)}
            />
          ))}
        </ul>
      )}

      {/* Quick add */}
      <QuickAdd onAdd={addQuick} showAIButton={subtasks.length === 0} onAIClick={() => setAiOpen(true)} />

      {/* All-done banner — small celebration, not an alert */}
      {allDone && showBanner && (
        <div
          className="rounded-lg bg-forest/[0.05] px-4 py-5 space-y-3"
          data-testid="all-done-banner"
        >
          <div className="text-sm text-ink leading-relaxed">
            Svi koraci su završeni. Želiš li da završiš i glavni zadatak?
          </div>
          <div className="flex gap-2">
            <button
              className="btn-primary text-xs"
              onClick={() => { onCompleteMain?.(); setShowBanner(false); }}
              data-testid="banner-complete-main"
            >
              Završi task
            </button>
            <button className="btn-ghost text-xs" onClick={() => setShowBanner(false)} data-testid="banner-later">
              Kasnije
            </button>
          </div>
        </div>
      )}

      <AIBreakdownDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        taskId={task?.id}
        onSaved={async () => { await refresh(); onChanged?.(); }}
      />
    </section>
  );
}


function QuickAdd({ onAdd, showAIButton, onAIClick }) {
  const [text, setText] = useState("");
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 flex items-center gap-2 border border-dashed border-line rounded-md px-2 py-1.5">
        <Plus className="w-4 h-4 text-ink-muted" />
        <input
          data-testid="subtask-quick-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { onAdd(text); setText(""); }
          }}
          placeholder="Napiši sledeći sitan korak..."
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-muted"
        />
      </div>
      {showAIButton && (
        <button
          type="button"
          onClick={onAIClick}
          data-testid="subtask-ai-btn"
          className="text-xs text-forest hover:underline inline-flex items-center gap-1"
        >
          <Sparkles className="w-3 h-3" /> Razloži na korake
        </button>
      )}
    </div>
  );
}


function SubtaskRow({ s, isNext, internalContacts, contactsById, onToggle, onRemove, onSetNext, onPatch, onDragStart, onDrop }) {
  const [showDetails, setShowDetails] = useState(!!(s.assignee_contact_id || s.due_date));
  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      data-testid={`subtask-row-${s.id}`}
      className={`text-sm rounded-md hover:bg-surface-secondary ${isNext ? "bg-forest/5" : ""}`}
    >
      <div className="flex items-start gap-2 px-2 py-1.5">
        <GripVertical className="w-4 h-4 mt-0.5 text-ink-muted cursor-grab" aria-hidden />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`subtask-check-${s.id}`}
          className="mt-0.5"
          aria-label="Toggle"
        >
          {s.done ? <CheckCircle2 className="w-4 h-4 text-forest" /> : <Circle className="w-4 h-4 text-ink-muted" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`${s.done ? "line-through text-ink-muted" : "text-ink"}`}>
            {s.title}
            {isNext && <span className="ml-2 text-[10px] uppercase text-forest">Sledeći</span>}
          </div>
          {!showDetails && !s.done && (
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              data-testid={`subtask-details-open-${s.id}`}
              className="text-[11px] text-forest hover:underline"
            >
              + Dodaj detalje
            </button>
          )}
          {showDetails && (
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <Select
                value={s.assignee_contact_id || "none"}
                onValueChange={(v) => onPatch({ assignee_contact_id: v === "none" ? null : v })}
              >
                <SelectTrigger className="bg-white h-8 text-xs">
                  <SelectValue placeholder="Odgovorna osoba" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">— bez —</SelectItem>
                  {internalContacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="date"
                value={s.due_date || ""}
                onChange={(e) => onPatch({ due_date: e.target.value || null })}
                className="h-8 text-xs"
                data-testid={`subtask-due-${s.id}`}
              />
            </div>
          )}
        </div>
        {!s.done && !isNext && (
          <button
            type="button"
            onClick={onSetNext}
            data-testid={`subtask-setnext-${s.id}`}
            className="text-[11px] text-forest hover:underline whitespace-nowrap"
          >
            Radi prvo
          </button>
        )}
        <button
          type="button"
          onClick={onRemove}
          data-testid={`subtask-remove-${s.id}`}
          className="text-ink-muted hover:text-terracotta"
          aria-label="Ukloni"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </li>
  );
}


function AIBreakdownDialog({ open, onOpenChange, taskId, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setSteps([]); return; }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post(`/tasks/${taskId}/subtasks/ai-breakdown`);
        setSteps(data.proposed_steps || []);
      } catch (e) { toast.error(formatApiError(e)); onOpenChange(false); }
      setLoading(false);
    })();
  }, [open, taskId, onOpenChange]);

  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...steps];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    setSteps(next);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const title of steps) {
        if (title.trim()) await api.post(`/tasks/${taskId}/subtasks`, { title: title.trim() });
      }
      toast.success(`Sačuvano ${steps.length} koraka`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ai-breakdown-dialog" className="bg-white max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-forest" /> AI predlog koraka
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-ink-muted py-8 text-center">Razmišljam...</div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-ink-muted">Uredi, izbriši ili promeni redosled pre nego što sačuvaš. Ništa se ne čuva automatski.</p>
            <ul className="space-y-2">
              {steps.map((s, i) => (
                <li key={i} data-testid={`ai-step-${i}`} className="flex items-center gap-2">
                  <button type="button" onClick={() => moveUp(i)} disabled={i === 0} className="text-xs text-ink-muted disabled:opacity-30" aria-label="Podigni">↑</button>
                  <Input
                    value={s}
                    onChange={(e) => setSteps(steps.map((x, j) => (j === i ? e.target.value : x)))}
                    className="flex-1"
                  />
                  <button type="button" onClick={() => setSteps(steps.filter((_, j) => j !== i))} className="text-ink-muted hover:text-terracotta" aria-label="Ukloni">
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex gap-2 justify-end">
              <button className="btn-ghost" onClick={() => onOpenChange(false)} data-testid="ai-discard">Odbaci</button>
              <button className="btn-primary" onClick={saveAll} disabled={saving || steps.length === 0} data-testid="ai-save-all">
                {saving ? "Čuvam..." : "Sačuvaj sve"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
